/* Auto-resize measurement for Textarea's `autoResize` option (engine tier,
 * not exported from the barrel — Textarea is its only consumer).
 *
 * Grows from `rows` (the browser's own wrap) to fit content by clearing the
 * explicit height and re-reading `scrollHeight` — the same technique
 * ReportEditorPanel and Sipoc's textareas already hand-roll today (they move
 * onto this option in the follow-up adoption PR). `maxRows` caps it: rather
 * than parse `getComputedStyle` for line-height/padding, the element's own
 * `rows` attribute is swapped to `maxRows` for one read of `clientHeight` —
 * the browser's real row math, under whatever font, size and variant classes
 * are already on the element — then restored. `scrollHeight`/`clientHeight`
 * exclude the border though, and every variant renders border-box with a
 * real border, so the border height (read once via `getComputedStyle`) is
 * added back — the same compensation ReportEditorPanel's `+ 2` hand-rolls. */
export function measureAutoResize(
  el: HTMLTextAreaElement,
  maxRows: number | undefined,
): void {
  // `scrollHeight`/`clientHeight` are content + padding only — never the
  // border — but every Textarea variant renders with box-sizing: border-box
  // and a real (if transparent, on ghost) border, so `style.height` (a
  // border-box total) needs that border added back or the box is undersized
  // by its width, same as ReportEditorPanel's hand-rolled `+ 2`.
  const style = getComputedStyle(el);
  const borderHeight =
    (parseFloat(style.borderTopWidth) || 0) +
    (parseFloat(style.borderBottomWidth) || 0);
  el.style.height = "auto";
  const fullHeight = el.scrollHeight + borderHeight;
  if (maxRows === undefined) {
    el.style.height = `${fullHeight}px`;
    el.style.overflowY = "";
    return;
  }
  const rows = el.rows;
  el.rows = maxRows;
  const maxHeight = el.clientHeight + borderHeight;
  el.rows = rows;
  if (fullHeight > maxHeight) {
    el.style.height = `${maxHeight}px`;
    el.style.overflowY = "auto";
  } else {
    el.style.height = `${fullHeight}px`;
    el.style.overflowY = "";
  }
}
